/**
 * "Stock reconciliado" (v2.2 findings.md #188).
 *
 * The recorded figures never change. An exact count (`FishStockLog.method
 * === 'count'`) that differs from the stock Sunday expected leaves a
 * "diferencia de conteo": fish lost or gained that no recorded death,
 * harvest or move explains. The reconciled view spreads that difference
 * evenly per day back to where it started — the lot's previous exact count,
 * else the lot's first day, or a later date the office set on the count
 * (`metadata.reconcileFromDate`) — so the weeks before the count show a
 * stock closer to reality.
 *
 * Expected stock at the count = end-of-day stock of the previous day minus
 * the deaths of the count day recorded before it, so with the count-day
 * rule (findings #203: the count is the stock when taken, later deaths of
 * that day come off):
 *   difference = EOD(count day) − EOD(previous day) + deaths(count day)
 *
 * Pure and side-effect free.
 */
import type { FishStockLog } from '../types/production'
import type { BiomassDataRow } from './productionUnitTable'
import { toLocalCalendarDayKey } from './dateNormalization'
import { farmTimeZone } from './farmTime'
import { daysBetween } from './lotProjection'

export const RECONCILE_FROM_DATE_METADATA_KEY = 'reconcileFromDate'

export type CountDifference = {
  logId: string
  groupKey: string
  /** Farm calendar day of the exact count. */
  day: string
  countedStock: number
  expectedStock: number
  difference: number
  /** difference ÷ expected × 100. */
  differencePct: number | null
  /** First day of the spread (exclusive): the day the difference starts from. */
  fromDay: string
  /** The earliest start the office can choose: the previous exact count or the lot's first day. */
  floorDay: string
  /** True when the office set the start on the count. */
  fromDayIsCustom: boolean
}

export type StockReconciliation = {
  /** Per production row id: reconciled stock (only rows the spread touches differ from the recorded stock). */
  reconciledStockByRowId: Map<string, number | null>
  /** Per production row id on a count day: that count's difference. */
  differenceByRowId: Map<string, CountDifference>
  differences: CountDifference[]
}

const parseNumber = (raw: unknown): number | null => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (typeof raw !== 'string' || !raw.trim()) return null
  const parsed = Number(raw.replaceAll(',', '.').trim())
  return Number.isFinite(parsed) ? parsed : null
}

/** Same group key as `buildFishStockDerivedByRowId` (reportDatasets.ts). */
export const stockGroupKey = (entity: { lotId?: string | null; lot?: string | null }): string => {
  const lotId = String(entity.lotId ?? '').trim()
  return lotId ? `id:${lotId}` : `label:${String(entity.lot ?? '').trim().toLowerCase()}`
}

const isIsoDay = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)

type Day = { day: string; rowIds: string[]; eod: number | null; deaths: number }

export const buildStockReconciliation = (args: {
  rows: readonly BiomassDataRow[]
  /** Recorded (mortality-aware, carried-forward) stock per row id. */
  derivedStockByRowId: ReadonlyMap<string, number | null>
  fishStockLogs: readonly FishStockLog[]
}): StockReconciliation => {
  const reconciledStockByRowId = new Map<string, number | null>()
  const differenceByRowId = new Map<string, CountDifference>()
  const differences: CountDifference[] = []

  // Days per lot group, in order.
  const daysByGroup = new Map<string, Map<string, Day>>()
  for (const row of args.rows) {
    if (!row || typeof row.id !== 'string' || !row.id) continue
    const groupKey = stockGroupKey(row)
    if (groupKey === 'label:') continue
    const day = toLocalCalendarDayKey(String(row.date ?? ''), farmTimeZone(String(row.farmId ?? '')))
    if (!day) continue
    let days = daysByGroup.get(groupKey)
    if (!days) {
      days = new Map()
      daysByGroup.set(groupKey, days)
    }
    const entry = days.get(day) ?? { day, rowIds: [], eod: null, deaths: 0 }
    entry.rowIds.push(row.id)
    const stock = args.derivedStockByRowId.get(row.id)
    if (stock !== undefined && stock !== null) entry.eod = stock
    // Every row of a (lot, day) carries the same canonical daily sum.
    entry.deaths = Math.max(entry.deaths, parseNumber(row.dailyMortality) ?? 0)
    days.set(day, entry)
    reconciledStockByRowId.set(row.id, stock ?? null)
  }

  // Exact counts per group and day (latest log of the day wins).
  const countsByGroup = new Map<string, Map<string, FishStockLog>>()
  for (const log of args.fishStockLogs) {
    if (log.method !== 'count') continue
    const groupKey = stockGroupKey(log)
    if (groupKey === 'label:') continue
    const day = toLocalCalendarDayKey(log.recordedAt, farmTimeZone(log.farmId))
    if (!day) continue
    let byDay = countsByGroup.get(groupKey)
    if (!byDay) {
      byDay = new Map()
      countsByGroup.set(groupKey, byDay)
    }
    const existing = byDay.get(day)
    if (!existing || log.recordedAt.localeCompare(existing.recordedAt) > 0) byDay.set(day, log)
  }

  countsByGroup.forEach((countsByDay, groupKey) => {
    const days = daysByGroup.get(groupKey)
    if (!days) return
    const ordered = Array.from(days.values()).sort((a, b) => a.day.localeCompare(b.day))
    const countDays = Array.from(countsByDay.keys()).sort()
    const firstDay = ordered[0]?.day

    countDays.forEach((countDay, countIndex) => {
      const log = countsByDay.get(countDay)!
      const index = ordered.findIndex((entry) => entry.day === countDay)
      if (index <= 0) return
      const today = ordered[index]
      const previous = ordered.slice(0, index).reverse().find((entry) => entry.eod !== null)
      if (!previous || previous.eod === null || today.eod === null) return

      const difference = today.eod - previous.eod + today.deaths
      const countedStock = parseNumber(log.countedStock) ?? today.eod
      const expectedStock = countedStock - difference
      // The spread never reaches past the previous exact count.
      const previousCountDay = countIndex > 0 ? countDays[countIndex - 1] : undefined
      const floorDay = previousCountDay ?? firstDay ?? countDay
      const custom = log.metadata?.[RECONCILE_FROM_DATE_METADATA_KEY]
      const customDay = isIsoDay(custom) && custom < countDay ? (custom < floorDay ? floorDay : custom) : undefined
      const fromDay = customDay ?? floorDay

      const result: CountDifference = {
        logId: log.id,
        groupKey,
        day: countDay,
        countedStock,
        expectedStock,
        difference,
        differencePct: expectedStock > 0 ? (difference / expectedStock) * 100 : null,
        fromDay,
        floorDay,
        fromDayIsCustom: customDay !== undefined
      }
      differences.push(result)
      today.rowIds.forEach((rowId) => differenceByRowId.set(rowId, result))
      if (difference === 0) return

      const span = daysBetween(fromDay, countDay)
      if (!(span > 0)) return
      for (const entry of ordered) {
        if (entry.day <= fromDay || entry.day >= countDay || entry.eod === null) continue
        const share = daysBetween(fromDay, entry.day) / span
        entry.rowIds.forEach((rowId) => {
          const current = reconciledStockByRowId.get(rowId)
          if (current === null || current === undefined) return
          reconciledStockByRowId.set(rowId, Math.max(0, Math.round(current + difference * share)))
        })
      }
    })
  })

  return { reconciledStockByRowId, differenceByRowId, differences }
}
